import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import AuthService from '../../services/authService';

const LeadClaimModal = ({ userId, onClose, onClaimed, isDemoAccount = false }) => {
  // "Sarah Chen" -> "Sarah C." — the full name is what the agent pays for.
  // Same helper as AvailableLeadModal; keep the two in sync.
  const formatDisplayName = (name) => {
    if (!name) return 'Anonymous';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
  };

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [agentPoints, setAgentPoints] = useState(0);

  // Light mode permanent (dark mode removed from portal)
  const [isLightMode] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back-end-x7tx.onrender.com';

  // Must stay in sync with backend utils/pointsConfig.js
  const POINTS_COST = {
    flex: 10,
    bronze: 10,
    silver: 25,
    gold: 45,
    platinum: 65
  };

  // Discounted pricing for STANDING users (unclaimed 90+ days)
  const STANDING_POINTS_COST = {
    flex: 7,
    bronze: 7,
    silver: 12,
    gold: 20,
    platinum: 30
  };

  const STANDING_THRESHOLD_DAYS = 90;

  const getPointsCost = (tier, createdAt) => {
    const tierLower = tier?.toLowerCase();
    if (createdAt) {
      const daysSinceCreation = (Date.now() - new Date(createdAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceCreation >= STANDING_THRESHOLD_DAYS) {
        return STANDING_POINTS_COST[tierLower] ?? POINTS_COST[tierLower] ?? 1;
      }
    }
    return POINTS_COST[tierLower] ?? 1;
  };

  const TIER_EMOJI = {
    flex: '⚪',
    bronze: '🥉',
    silver: '🥈',
    gold: '🥇',
    platinum: '💎'
  };

  // Helper function to convert income to monthly
  const getMonthlyIncome = (income, frequency) => {
    if (!income || !frequency) return null;
    
    switch (frequency.toLowerCase()) {
      case 'weekly':
        return Math.round(income * 52 / 12); // 52 weeks / 12 months
      case 'biweekly':
        return Math.round(income * 26 / 12); // 26 pay periods / 12 months
      case 'monthly':
        return income;
      default:
        return income;
    }
  };

  useEffect(() => {
    fetchUserDetails();
    fetchAgentPoints();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      const token = AuthService.getToken();
      console.log('🔍 Fetching user details for userId:', userId);
      
      const response = await fetch(`${API_BASE_URL}/api/agents/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUser(data.data);
      } else {
        setError(data.message || 'Failed to load user details');
      }
      setLoading(false);
    } catch (err) {
      console.error('❌ Error fetching user:', err);
      setError('Failed to load user details');
      setLoading(false);
    }
  };

  const fetchAgentPoints = async () => {
    try {
      const token = AuthService.getToken();
      const currentUser = AuthService.getCurrentUser();
      
      if (!currentUser || !currentUser._id) {
        console.error('No current user found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/agents/${currentUser._id}/points-history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success && data.data.currentBalance !== undefined) {
        setAgentPoints(data.data.currentBalance);
      } else {
        if (currentUser.pointsBalance !== undefined) {
          setAgentPoints(currentUser.pointsBalance);
        }
      }
    } catch (err) {
      console.error('❌ Error fetching agent points:', err);
      const currentUser = AuthService.getCurrentUser();
      if (currentUser && currentUser.pointsBalance !== undefined) {
        setAgentPoints(currentUser.pointsBalance);
      }
    }
  };

  const handleClaim = async () => {
    if (!user) return;
    // Demo accounts cannot claim — silently no-op as a safety net.
    // (This modal should not be opened for demo accounts in the first place.)
    if (isDemoAccount) return;

    const pointsCost = getPointsCost(user.leadTier, user.createdAt);

    if (agentPoints < pointsCost) {
      setError(`Insufficient points. You need ${pointsCost} points but only have ${agentPoints}.`);
      return;
    }

    setClaiming(true);
    setError('');

    try {
      const token = AuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/agents/claim-lead-portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: userId
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('User claimed successfully! View full details in User Management screen.');
        setAgentPoints(data.data.points.remaining);
        
        const currentUser = AuthService.getCurrentUser();
        currentUser.pointsBalance = data.data.points.remaining;
        localStorage.setItem('user', JSON.stringify(currentUser));

        setTimeout(() => {
          if (onClaimed) onClaimed();
          onClose();
        }, 3000);
      } else {
        setError(data.message || 'Failed to claim user');
      }
    } catch (err) {
      console.error('Error claiming user:', err);
      setError('Failed to claim user. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    const styles = getStyles(isLightMode);
    return createPortal(
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.loading}>Loading...</div>
        </div>
      </div>,
      document.body
    );
  }

  if (!user) {
    const styles = getStyles(isLightMode);
    return createPortal(
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>User Not Found</h2>
            <button onClick={onClose} style={styles.closeButton}>✕</button>
          </div>
          <p style={styles.errorText}>{error || 'This user is no longer available'}</p>
          <button onClick={onClose} style={styles.closeOnlyButton}>Close</button>
        </div>
      </div>,
      document.body
    );
  }

  const pointsCost = getPointsCost(user.leadTier, user.createdAt);
  const canAfford = agentPoints >= pointsCost;
  const isAvailable = user.leadStatus === 'available';
  
  // Get styles based on current theme
  const styles = getStyles(isLightMode);

  return createPortal(
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>New User Profile</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        {error && (
          <div style={styles.errorBox}>
            {error}
          </div>
        )}

        {success && (
          <div style={styles.successBox}>
            {success}
          </div>
        )}

        <div style={styles.content}>
          <p style={styles.subtitle}>A new user has registered nearby! Their details are listed below.</p>

          {/* User Info Card */}
          <div style={styles.infoCard}>
            <div style={styles.infoRow}>
              <div style={styles.label}>Name</div>
              <div style={styles.value}>{formatDisplayName(user.name)}</div>
            </div>
            <div style={styles.infoRow}>
              <div style={styles.label}>User Tier</div>
              <div style={styles.tierValue}>
                {TIER_EMOJI[user.leadTier?.toLowerCase()] || '❓'} {(user.leadTier || 'unassigned').toUpperCase()}
              </div>
            </div>
            {user.leadStatus === 'claimed' && user.assignedAgent && (
              <div style={styles.infoRow}>
                <div style={styles.label}>Phone</div>
                <div style={styles.value}>***-***-****</div>
              </div>
            )}
            {isAvailable && (
              <div style={styles.infoRow}>
                <div style={styles.label}>Points</div>
                <div style={styles.pointsValue}>{pointsCost} Points</div>
              </div>
            )}
            {user.onboardingData?.monthlyIncome && (
              <div style={styles.infoRow}>
                <div style={styles.label}>Monthly Income</div>
                <div style={styles.value}>
                  ${getMonthlyIncome(
                    user.onboardingData.monthlyIncome, 
                    user.onboardingData.incomeFrequency
                  )?.toLocaleString()}/mo
                </div>
              </div>
            )}
            {user.onboardingData?.creditScore && (
              <div style={styles.infoRow}>
                <div style={styles.label}>Credit Score</div>
                <div style={styles.value}>{user.onboardingData.creditScore}</div>
              </div>
            )}
            {user.onboardingData?.budgetExpense && (
              <div style={styles.infoRow}>
                <div style={styles.label}>Budget</div>
                <div style={styles.value}>
                  ${user.onboardingData.budgetExpense.toLocaleString()}/mo
                </div>
              </div>
            )}
            {user.onboardingData?.livingSituation && (
              <div style={styles.infoRow}>
                <div style={styles.label}>Living Situation</div>
                <div style={styles.value}>
                  {user.onboardingData.livingSituation.charAt(0).toUpperCase() + user.onboardingData.livingSituation.slice(1)}
                </div>
              </div>
            )}
            <div style={styles.infoRow}>
              <div style={styles.label}>User Location</div>
              <div style={styles.value}>{user.location || 'Local'}</div>
            </div>
          </div>

          {/* Status Warning */}
          {!isAvailable && (
            <div style={styles.statusBox}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff6b6b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <div style={styles.statusText}>
                This user has already been claimed
              </div>
            </div>
          )}

          {/* Insufficient Points Warning */}
          {isAvailable && !canAfford && (
            <div style={styles.statusBox}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff6b6b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <div style={styles.statusText}>
                Insufficient points. You need {pointsCost} points but only have {agentPoints}.
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          {isDemoAccount ? (
            <button
              disabled
              title="Claiming is disabled for demo accounts"
              style={{
                ...styles.claimButton,
                ...styles.claimButtonDisabled,
                cursor: 'not-allowed'
              }}
            >
              Claim
            </button>
          ) : isAvailable ? (
            <button
              onClick={handleClaim}
              disabled={claiming || !canAfford}
              style={{
                ...styles.claimButton,
                ...((!canAfford || claiming) && styles.claimButtonDisabled)
              }}
            >
              {claiming ? 'Claiming...' : 'Claim'}
            </button>
          ) : (
            <button onClick={onClose} style={styles.closeOnlyButton}>
              Close
            </button>
          )}
        </div>

        <div style={styles.footer}>
          This message was automatically generated by the Lauft intake system.
        </div>
      </div>
    </div>,
    document.body
  );
};

const getStyles = (isLightMode) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff',
    background: '#ffffff',
    borderRadius: '16px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.18)',
  },
  loading: {
    padding: '60px',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '1.05rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 22px',
    borderBottom: '1px solid #f0f0f0',
  },
  title: {
    fontSize: '1.2rem',
    fontWeight: '700',
    margin: 0,
    color: '#111',
    letterSpacing: '-0.01em',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    fontSize: '20px',
    cursor: 'pointer',
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: '24px',
  },
  subtitle: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.9375rem',
    marginBottom: '20px',
    lineHeight: '1.5',
  },
  infoCard: {
    backgroundColor: isLightMode ? '#f9f8f5' : 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px',
    padding: '0',
    marginBottom: '20px',
    border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: isLightMode ? '1px solid rgba(26, 26, 26, 0.08)' : '1px solid rgba(255, 255, 255, 0.08)',
  },
  label: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.5)',
    fontSize: '0.875rem',
    fontWeight: '400',
  },
  value: {
    color: isLightMode ? '#1a1a1a' : '#ffffff',
    fontSize: '0.9375rem',
    fontWeight: '500',
    textAlign: 'right',
  },
  tierValue: {
    color: '#111',
    fontSize: '0.9375rem',
    fontWeight: '600',
    textAlign: 'right',
  },
  pointsValue: {
    color: '#111',
    fontSize: '0.9375rem',
    fontWeight: '600',
    textAlign: 'right',
  },
  statusBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '9px',
    padding: '11px 14px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  statusText: {
    color: '#b91c1c',
    fontSize: '0.875rem',
    margin: 0,
    lineHeight: '1.5',
    flex: 1,
    fontWeight: '500',
  },
  actions: {
    padding: '0 24px 24px 24px',
    display: 'flex',
    justifyContent: 'center',
  },
  claimButton: {
    backgroundColor: '#ffffff',
    color: '#111',
    border: '1px solid #e5e7eb',
    padding: '10px 24px',
    borderRadius: '10px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    maxWidth: '220px',
    transition: 'all 0.12s',
    letterSpacing: '-0.005em',
  },
  claimButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  closeOnlyButton: {
    backgroundColor: '#ffffff',
    color: '#111',
    border: '1px solid #e5e7eb',
    padding: '10px 24px',
    borderRadius: '10px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    maxWidth: '220px',
    transition: 'all 0.12s',
  },
  footer: {
    textAlign: 'center',
    padding: '14px 22px',
    borderTop: '1px solid #f0f0f0',
    color: '#9ca3af',
    fontSize: '0.72rem',
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    padding: '11px 14px',
    margin: '0 22px 14px 22px',
    fontSize: '0.875rem',
    fontWeight: '500',
    borderRadius: '9px',
  },
  successBox: {
    backgroundColor: '#ecfdf5',
    border: '1px solid #a7f3d0',
    color: '#047857',
    padding: '11px 14px',
    margin: '0 22px 14px 22px',
    fontSize: '0.875rem',
    fontWeight: '500',
    borderRadius: '9px',
  },
  errorText: {
    color: '#b91c1c',
    textAlign: 'center',
    padding: '20px',
    margin: 0,
    fontSize: '0.9rem',
  },
});

export default LeadClaimModal;